import { useCallback } from "react";
import { useNavigate } from "react-router";

/**
 * Faz um elemento que NÃO é um `<a>` (ex.: card com botões aninhados, linha de
 * tabela) se comportar como link: cmd/ctrl/shift+clique ou clique do meio abrem
 * o destino numa nova aba; clique simples navega dentro do SPA.
 *
 * Para elementos folha (texto/ícone que "parece link", sem controles aninhados),
 * prefira o `<Link>` do react-router, que já renderiza um `<a href>` real com
 * esse comportamento nativo (e ainda habilita "copiar link", botão direito etc.).
 *
 * Uso:
 *   const linkProps = useInternalLink();
 *   <div {...linkProps(`/vagas/${id}`)}>...</div>
 *
 * Passe `onPlainClick` quando o clique simples já tem lógica própria que deve
 * ser preservada (ex.: handler existente do card).
 */
export function useInternalLink() {
  const navigate = useNavigate();

  return useCallback(
    (to: string, onPlainClick?: (e: React.MouseEvent) => void) => ({
      // Clique esquerdo (com ou sem modificadores).
      onClick: (e: React.MouseEvent) => {
        if (e.defaultPrevented) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey) {
          e.preventDefault();
          window.open(to, "_blank", "noopener");
          return;
        }
        if (onPlainClick) onPlainClick(e);
        else navigate(to);
      },
      // Clique do meio (botão 1) não dispara onClick; tratamos no onAuxClick.
      onAuxClick: (e: React.MouseEvent) => {
        if (e.button !== 1 || e.defaultPrevented) return;
        e.preventDefault();
        window.open(to, "_blank", "noopener");
      },
    }),
    [navigate],
  );
}
